import type {
  SalesTrendData,
  SalesTrendPattern,
  SalesTrendTargetDateEntry,
} from '@/lib/types/ds/prediction-error-evaluation';

/** 系列色。同時表示できるパターン数の上限も兼ねる（= 6 本） */
export const SERIES_COLORS = [
  '#2563eb',
  '#f97316',
  '#16a34a',
  '#9333ea',
  '#dc2626',
  '#0891b2',
] as const;

/** パターンを一意に識別する3パラメータ */
export type PatternParams = Pick<SalesTrendPattern, 'train_term' | 'tolerance' | 'max_data_count'>;

/** 系列キー・state 用の識別子（例: `tt14_tl0.4_mdc7`） */
export function patternKey(pattern: PatternParams): string {
  return `tt${pattern.train_term}_tl${pattern.tolerance}_mdc${pattern.max_data_count}`;
}

/** 画面表示用のラベル（例: `tt=14 tl=0.4 mdc=7`） */
export function patternLabel(pattern: PatternParams): string {
  return `tt=${pattern.train_term} tl=${pattern.tolerance} mdc=${pattern.max_data_count}`;
}

/** 指定基準日のエントリ。不一致・未指定なら先頭、空なら undefined */
export function selectEntry(
  data: SalesTrendData,
  selectedTargetDate?: string,
): SalesTrendTargetDateEntry | undefined {
  const entries = data.by_target_date;
  if (entries.length === 0) return undefined;
  if (selectedTargetDate !== undefined) {
    const matched = entries.find((entry) => entry.target_date === selectedTargetDate);
    if (matched) return matched;
  }
  return entries[0];
}

/** 既定で表示するパターンキー。is_best を優先し、無ければ先頭 */
export function defaultPatternKey(entry: SalesTrendTargetDateEntry): string | undefined {
  const pattern = entry.patterns.find((p) => p.is_best) ?? entry.patterns[0];
  return pattern ? patternKey(pattern) : undefined;
}

/** Recharts に渡す1行（date + `<patternKey>__actual` / `<patternKey>__pred`） */
export interface SalesTrendChartRow {
  date: string;
  [seriesKey: string]: string | number | null;
}

/** 横軸 dates に沿って、表示対象パターンの実績・予測累積を1行ずつに畳み込む */
export function toChartRows(
  entry: SalesTrendTargetDateEntry,
  visible: SalesTrendPattern[],
): SalesTrendChartRow[] {
  const targetDateIndex = entry.train_days;

  return entry.dates.map((date, i) => {
    const row: SalesTrendChartRow = { date };
    for (const pattern of visible) {
      const key = patternKey(pattern);
      row[`${key}__actual`] = pattern.actual_cumulative[i] ?? null;
      row[`${key}__pred`] = predValueAt(pattern, targetDateIndex, i);
    }
    return row;
  });
}

/**
 * 基準日より前は null（線を引かない）、基準日は実績と同値（実績カーブから分岐させる）、
 * 以降は pred_cumulative。BE データの配列長が不整合なら null で線を途切れさせて破損を可視化する
 */
function predValueAt(
  pattern: SalesTrendPattern,
  targetDateIndex: number,
  index: number,
): number | null {
  if (index < targetDateIndex) return null;
  if (index === targetDateIndex) return pattern.actual_cumulative[targetDateIndex] ?? null;
  return pattern.pred_cumulative[index - targetDateIndex - 1] ?? null;
}

// ---------------------------------------------------------------------------
// 共通 SKU モード（全パターンの予測対象 SKU の積集合に揃えて比較する）
// ---------------------------------------------------------------------------

/** 共通 SKU モードの実績系列 dataKey。全パターンで共有する1本 */
export const COMMON_ACTUAL_KEY = 'common__actual';

/** 共通 SKU モードの予測系列 dataKey（例: `tt14_tl0.4_mdc7__common_pred`） */
export function commonPredKey(pattern: PatternParams): string {
  return `${patternKey(pattern)}__common_pred`;
}

/** 共通 SKU モードのカバレッジ（どれだけ SKU を除外して比較しているか） */
export interface CommonSkuCoverage {
  /** 積集合の SKU 数 */
  commonSkuCount: number;
  /** パターン別 sku_count の最大値（= 最大母集団） */
  maxSkuCount: number;
  /** 最大母集団から除外される SKU 数（maxSkuCount - commonSkuCount、負にはしない） */
  excludedSkuCount: number;
  /** 最大母集団に対する共通 SKU の割合（%）。maxSkuCount が 0 のときは 0 */
  coveragePercent: number;
}

/** 共通 SKU モードのカバレッジ。common_sku_count が無い（旧ジョブ）なら undefined */
export function commonSkuCoverage(entry: SalesTrendTargetDateEntry): CommonSkuCoverage | undefined {
  const commonSkuCount = entry.common_sku_count;
  if (commonSkuCount === undefined) return undefined;

  const maxSkuCount = entry.patterns.reduce((max, pattern) => Math.max(max, pattern.sku_count), 0);
  return {
    commonSkuCount,
    maxSkuCount,
    // 積集合が最大母集団を超えることは本来ありえないが、BE データ破損時に負の除外数を出さない
    excludedSkuCount: Math.max(0, maxSkuCount - commonSkuCount),
    coveragePercent: maxSkuCount === 0 ? 0 : (commonSkuCount / maxSkuCount) * 100,
  };
}

/**
 * 共通 SKU モードのトグルを出してよいか。
 * common_* が揃っていて、かつ両モードが同一にならない（母集団が異なるパターンがある）ときだけ true
 */
export function isCommonModeAvailable(entry: SalesTrendTargetDateEntry): boolean {
  const commonSkuCount = entry.common_sku_count;
  if (commonSkuCount === undefined || entry.common_actual_cumulative === undefined) return false;
  return entry.patterns.some((pattern) => pattern.sku_count !== commonSkuCount);
}

/**
 * 共通 SKU モードのチャート行。実績は {@link COMMON_ACTUAL_KEY} の1本のみで、
 * 予測は表示対象パターンごとに {@link commonPredKey} で並ぶ。
 * 基準日前は予測 null / 基準日は実績と同値 / 以降は common_pred_cumulative（{@link toChartRows} と同規約）
 */
export function toCommonChartRows(
  entry: SalesTrendTargetDateEntry,
  visible: SalesTrendPattern[],
): SalesTrendChartRow[] {
  const targetDateIndex = entry.train_days;
  const commonActual = entry.common_actual_cumulative;

  return entry.dates.map((date, i) => {
    const row: SalesTrendChartRow = { date };
    row[COMMON_ACTUAL_KEY] = commonActual?.[i] ?? null;
    for (const pattern of visible) {
      row[commonPredKey(pattern)] = commonPredValueAt(pattern, commonActual, targetDateIndex, i);
    }
    return row;
  });
}

/** {@link predValueAt} と同規約。分岐の起点だけが共通実績（common_actual_cumulative）になる */
function commonPredValueAt(
  pattern: SalesTrendPattern,
  commonActual: number[] | undefined,
  targetDateIndex: number,
  index: number,
): number | null {
  if (index < targetDateIndex) return null;
  if (index === targetDateIndex) return commonActual?.[targetDateIndex] ?? null;
  return pattern.common_pred_cumulative?.[index - targetDateIndex - 1] ?? null;
}
